import React, {Suspense, useState} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useSearchParams} from 'react-router-dom';
import clsx from 'clsx';
import {diApi} from '@di-shared/api/di';
import type {ApiResponseTripTagListResponse, TripTagInfoResponse} from '@di-shared/api/di/__generated';
import {getYYYYMMddFormat} from '@di-shared/api/di/utils';
import {emitEventLog} from '@di-shared/features/analytics';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {HistoryDownloadFormProps} from '@di-shared/features/history/download/HistoryDownloadForm/index';
import HistoryLock from '@di-shared/features/history/setting/HistoryLock';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {addDate, endDateOfMonth, isSameYearAndMonth, startDateOfMonth, subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQuery} from '@tanstack/react-query';

interface HistoryFilterTagsSectionProps {
  selectedTagIds: Array<string>;
  onChangeTagIds(tagIds: Array<string>): void;
}

export const PERIOD = {
  SELECT_MONTH: 12,
  RECENT_30_DAYS: 30,
  RECENT_90_DAYS: 90,
};

enum PeriodType {
  SELECT_MONTH,
  RECENT_30_DAYS,
  RECENT_90_DAYS,
  CUSTOM_DAYS,
}

export default function ({vin}: HistoryDownloadFormProps) {
  // TBD 다운로드 기능
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <HistoryLock
          vin={vin}
          fallbackLoading={<FallbackLoading />}
          fallbackError={<FallbackError />}
        >
          <Inner vin={vin} />
        </HistoryLock>
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: HistoryDownloadFormProps) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const [form, setForm] = useState<{
    period: PeriodType | null;
    startDate: Date | null;
    endDate: Date | null;
  }>(updatePeriod(PeriodType.RECENT_30_DAYS));
  const [searchParams] = useSearchParams();
  const [selectedTagIds, setSelectedTagIds] = useState<Array<string>>(searchParams.getAll('tag').filter(Boolean));

  function updatePeriod(period: PeriodType) {
    switch (period) {
      case PeriodType.RECENT_30_DAYS:
        return {
          period,
          startDate: subDate(today, {days: PERIOD.RECENT_30_DAYS - 1}),
          endDate: today,
        };
      case PeriodType.RECENT_90_DAYS:
        return {
          period,
          startDate: subDate(today, {days: PERIOD.RECENT_90_DAYS - 1}),
          endDate: today,
        };
      case PeriodType.CUSTOM_DAYS:
        return {
          period,
          startDate: null,
          endDate: null,
        };
      default:
        return {
          period: null,
          startDate: null,
          endDate: null,
        };
    }
  }

  async function openSelectMonthBottomSheet() {
    const months = Array.from({length: PERIOD.SELECT_MONTH}).map((_, index) => subDate(today, {months: index}));
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date>(ROUTE_PATHS.BOTTOM_SHEET_HISTORY_SELECT_MONTH.getFullPath(), {
      months,
      selectedDate: form.period === PeriodType.SELECT_MONTH ? form.startDate : undefined,
    });

    if (!date) {
      return;
    }

    setForm({
      period: PeriodType.SELECT_MONTH,
      startDate: startDateOfMonth(date),
      endDate: isSameYearAndMonth(date, today) ? today : endDateOfMonth(date),
    });
  }

  async function openStartDatePicker() {
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.getFullPath(),
      {
        selectedDate: form.startDate,
        minDate: form.endDate
          ? subDate(form.endDate, {days: PERIOD.RECENT_90_DAYS - 1})
          : startDateOfMonth(subDate(today, {months: PERIOD.SELECT_MONTH - 1})),
        maxDate: form.endDate ? form.endDate : today,
      }
    );

    if (!date) {
      return;
    }

    setForm((prev) => ({
      period: prev.period,
      startDate: date,
      endDate: prev.endDate,
    }));
  }

  async function openEndDatePicker() {
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.getFullPath(),
      {
        selectedDate: form.endDate,
        minDate: form.startDate ? form.startDate : startDateOfMonth(subDate(today, {months: PERIOD.SELECT_MONTH - 1})),
        maxDate:
          form.startDate && form.startDate < subDate(today, {days: PERIOD.RECENT_90_DAYS - 1})
            ? addDate(form.startDate, {days: PERIOD.RECENT_90_DAYS - 1})
            : today,
      }
    );

    if (!date) {
      return;
    }

    setForm((prev) => ({
      period: prev.period,
      startDate: prev.startDate,
      endDate: date,
    }));
  }

  const [downloadExcel] = usePendingHandler(
    async () => {
      if (!form.startDate || !form.endDate) {
        return;
      }

      WebviewUtils.loading(true);
      const {token} = await diApi.driveHistoryExcel.driveHistoryExcelDownloadToken({
        vin,
        startDate: getYYYYMMddFormat(form.startDate),
        endDate: getYYYYMMddFormat(form.endDate),
        tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      });

      WebviewUtils.downloadUrl({
        fileName:
          `driving_${DashedDatetimeFormatter.format(form.startDate, 'date')}_${DashedDatetimeFormatter.format(form.endDate, 'date')}.xlsx`.replaceAll(
            '-',
            ''
          ),
        url: `${import.meta.env.VITE_DI_KR_API_URL}/drive-history/excel/file?token=${token}`,
        isOpenShare: true,
        mimeType: 'application/vnd.ms-excel',
        messages: {
          complete: t('T10170'),
          failure: t('T10171'),
        },
      });
      WebviewUtils.loading(false);
    },
    async (error) => {
      WebviewUtils.loading(false);
      WebviewUtils.showToast(t('T10171'));
    }
  );

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">
          <span className="blind">{t('T10089')}</span>
        </h1>
      </Header>
      <main className="content page_setting">
        <section className="section">
          <div className="section_heading">
            <h2 className="section_heading_title">{t('T10089')}</h2>
            <p className="section_heading_desc">{t('T10095')}</p>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('T10096')}</h3>
            <button
              className="btn_select"
              type="button"
              onClick={openSelectMonthBottomSheet}
            >
              {form.period !== PeriodType.SELECT_MONTH ? (
                <span className="select_placeholder">{t('T10192')}</span>
              ) : (
                <span className="select_value">{CldrDatetimeFormatter.format(form.startDate ? form.startDate : today, 'yMMM')}</span>
              )}
              <span
                className="ic ic_arrow_down_16px"
                aria-hidden="true"
              />
            </button>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('T10097')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === PeriodType.RECENT_30_DAYS})}
                    type="button"
                    onClick={() => setForm(updatePeriod(PeriodType.RECENT_30_DAYS))}
                  >
                    {t('T30082_$days', {days: PERIOD.RECENT_30_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === PeriodType.RECENT_90_DAYS})}
                    type="button"
                    onClick={() => setForm(updatePeriod(PeriodType.RECENT_90_DAYS))}
                  >
                    {t('T30082_$days', {days: PERIOD.RECENT_90_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': form.period === PeriodType.CUSTOM_DAYS})}
                    type="button"
                    onClick={() => setForm(updatePeriod(PeriodType.CUSTOM_DAYS))}
                  >
                    {t('T10165')}
                  </button>
                </li>
              </ul>
              <div className="select_date_box">
                <div className="btn_box">
                  <button
                    className={clsx('btn btn_full btn_outline btn_date', {
                      type_placeholder: form.period !== PeriodType.CUSTOM_DAYS,
                    })}
                    type="button"
                    disabled={form.period !== PeriodType.CUSTOM_DAYS}
                    onClick={() => openStartDatePicker()}
                  >
                    <span className="body_large_r text_date">
                      {form.startDate ? CldrDatetimeFormatter.format(form.startDate, 'medium') : t('T10166')}
                    </span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                  <span
                    className="body_large_b tilde"
                    role="none"
                  >
                    -
                  </span>
                  <button
                    className={clsx('btn btn_full btn_outline btn_date', {
                      type_placeholder: form.period !== PeriodType.CUSTOM_DAYS,
                    })}
                    type="button"
                    disabled={form.period !== PeriodType.CUSTOM_DAYS}
                    onClick={() => openEndDatePicker()}
                  >
                    <span className="body_large_r text_date">
                      {form.endDate ? CldrDatetimeFormatter.format(form.endDate, 'medium') : t('T10167')}
                    </span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
        <HistoryFilterTagsSection
          selectedTagIds={selectedTagIds}
          onChangeTagIds={setSelectedTagIds}
        />
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
            onClick={() => {
              setForm(updatePeriod(PeriodType.RECENT_30_DAYS));
              setSelectedTagIds([]);
            }}
          >
            {t('T10355')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            disabled={!form.startDate || !form.endDate}
            onClick={() => {
              emitEventLog({
                event: 'driving_records_download',
                form_step: 'download',
                click_text: t('T10099'),
                click_location: 'body',
              });

              downloadExcel();
            }}
          >
            {t('T10099')}
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryFilterTagsSection(props: HistoryFilterTagsSectionProps) {
  return (
    <section className="section">
      <ErrorBoundary fallback={<HistoryFilterTagsSectionErrorFallback />}>
        <Suspense fallback={<HistoryFilterTagsSectionLoading />}>
          <HistoryFilterTagsSectionInner {...props} />
        </Suspense>
      </ErrorBoundary>
    </section>
  );
}

function HistoryFilterTagsSectionInner({selectedTagIds, onChangeTagIds}: HistoryFilterTagsSectionProps) {
  const {t} = useInferableTranslation();

  const {data: tripTags} = useSuspenseQuery<ApiResponseTripTagListResponse, Error, Array<TripTagInfoResponse>>({
    queryKey: [getQueryKey(diApi.tripTags.getAllTags)],
    queryFn: () => diApi.tripTags.getAllTags(),
    select: ({data}) => data.tags,
  });

  const isAllTagsSelected = selectedTagIds.length === 0;

  function toggleTagSelection(tagId: string) {
    if (selectedTagIds.length === 0) {
      onChangeTagIds([tagId]);
      return;
    }

    if (selectedTagIds.includes(tagId)) {
      const nextTagIds = selectedTagIds.filter((id) => id !== tagId);

      onChangeTagIds(nextTagIds.length > 0 ? nextTagIds : []);
      return;
    }

    onChangeTagIds([...selectedTagIds, tagId]);
  }

  return (
    <>
      <div className="section_container_header">
        <h3 className="title_xxsmall">{t('T10354')}</h3>
      </div>
      <div className="tag_area">
        <button
          type="button"
          className={clsx('btn btn_round', {'is-selected': isAllTagsSelected})}
          onClick={() => onChangeTagIds([])}
        >
          <span
            className="ic ic_check_16px"
            aria-hidden="true"
          ></span>
          {t('T30587')}
        </button>
        {tripTags.map(({tagId, tagName}) => (
          <button
            key={tagId}
            type="button"
            className={clsx('btn btn_round', {'is-selected': selectedTagIds.includes(tagId)})}
            onClick={() => toggleTagSelection(tagId)}
          >
            <span
              className="ic ic_check_16px"
              aria-hidden="true"
            ></span>
            {tagName}
          </button>
        ))}
      </div>
    </>
  );
}

function HistoryFilterTagsSectionLoading() {
  const {t} = useInferableTranslation();

  return (
    <>
      <div className="section_container_header">
        <h3 className="title_xxsmall">{t('T10354')}</h3>
      </div>
      <div className="tag_area">
        {Array.from({length: 4}).map((_, index) => (
          <span
            key={`history-download-tag-skeleton-${index}`}
            className="btn btn_round skeleton"
            style={{width: 72, height: 32}}
            aria-hidden="true"
          />
        ))}
      </div>
    </>
  );
}

function HistoryFilterTagsSectionErrorFallback() {
  const {t} = useInferableTranslation();

  return (
    <>
      <div className="section_container_header">
        <h3 className="title_xxsmall">{t('T10354')}</h3>
      </div>
      <div className="trip_info">
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10175')}
          </span>
        </div>
      </div>
    </>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">
          <span className="blind">{t('T10089')}</span>
        </h1>
      </Header>
      <main className="content page_setting">
        <section className="section">
          <div className="section_heading">
            <h2
              className="section_heading_title skeleton"
              style={{width: '160px'}}
            >
              {t('T10089')}
            </h2>
            <p
              className="section_heading_desc skeleton"
              style={{width: '200px'}}
            >
              {t('T10095')}
            </p>
          </div>
          <div className="setting_box skeleton">
            <h3 className="title_xxsmall">{t('T10096')}</h3>
            <button
              className="btn_select"
              type="button"
            >
              <span className="select_placeholder">{t('T10192')}</span>
              <span
                className="ic ic_arrow_down_16px"
                aria-hidden="true"
              />
            </button>
          </div>
          <div className="setting_box skeleton">
            <h3 className="title_xxsmall">{t('T10097')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T30082_$days', {days: PERIOD.RECENT_30_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T30082_$days', {days: PERIOD.RECENT_90_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T10165')}
                  </button>
                </li>
              </ul>
              <div className="select_date_box">
                <div className="btn_box">
                  <button
                    className="btn btn_full btn_outline btn_date"
                    type="button"
                  >
                    <span className="body_large_r text_date">{t('T10166')}</span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                  <span
                    className="body_large_b tilde"
                    role="none"
                  >
                    -
                  </span>
                  <button
                    className="btn btn_full btn_outline btn_date"
                    type="button"
                  >
                    <span className="body_large_r text_date">{t('T10167')}</span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="section">
          <div className="section_container_header">
            <h3 className="title_xxsmall skeleton">{t('T10354')}</h3>
          </div>
          <div className="tag_area">
            {Array.from({length: 4}).map((_, index) => (
              <span
                key={`history-download-fallback-tag-skeleton-${index}`}
                className="btn btn_round skeleton"
                style={{width: 72, height: 32}}
                aria-hidden="true"
              />
            ))}
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
          >
            {t('T10355')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
          >
            {t('T10099')}
          </button>
        </div>
      </div>
    </div>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">
          <span className="blind">{t('T10089')}</span>
        </h1>
      </Header>
      <main className="content page_setting">
        <section className="section">
          <div className="section_heading">
            <h2 className="section_heading_title">{t('T10089')}</h2>
            <p className="section_heading_desc">{t('T10095')}</p>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('T10097')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T30082_$days', {days: PERIOD.RECENT_30_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T30082_$days', {days: PERIOD.RECENT_90_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T10165')}
                  </button>
                </li>
              </ul>
              <div className="select_date_box">
                <div className="btn_box">
                  <button
                    className="btn btn_full btn_outline btn_date"
                    type="button"
                  >
                    <span className="body_large_r text_date">{t('T10166')}</span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                  <span
                    className="body_large_b tilde"
                    role="none"
                  >
                    -
                  </span>
                  <button
                    className="btn btn_full btn_outline btn_date"
                    type="button"
                  >
                    <span className="body_large_r text_date">{t('T10167')}</span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('T10096')}</h3>
            <button
              className="btn_select"
              type="button"
            >
              <span className="select_placeholder">{t('T10192')}</span>
              <span
                className="ic ic_arrow_down_16px"
                aria-hidden="true"
              />
            </button>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
          >
            {t('T10355')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
          >
            {t('T10099')}
          </button>
        </div>
      </div>
    </div>
  );
}
